import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireAuth } from "./auth.server";
import process from "node:process";

async function getStripe() {
  const { default: Stripe } = await import("stripe");
  const key = process.env.STRIPE_SECRET_KEY;
  if (!key) throw new Error("Missing STRIPE_SECRET_KEY");
  return new Stripe(key);
}

async function getUserFromAuth() {
  const { user } = await requireAuth();
  const { getSupabaseAdmin } = await import("@/integrations/supabase/admin.server");
  const admin = getSupabaseAdmin();
  return { user, admin };
}

export const createCheckoutSession = createServerFn({ method: "POST" })
  .inputValidator(z.object({ origin: z.string().url() }))
  .handler(async ({ data }) => {
    const { user, admin } = await getUserFromAuth();
    const stripe = await getStripe();

    const { data: profile } = await admin
      .from("profiles")
      .select("stripe_customer_id, email")
      .eq("id", user.id)
      .maybeSingle();

    let customerId = profile?.stripe_customer_id ?? null;
    if (!customerId) {
      const customer = await stripe.customers.create({
        email: user.email ?? profile?.email ?? undefined,
        metadata: { user_id: user.id },
      });
      customerId = customer.id;
      await admin.from("profiles").update({ stripe_customer_id: customerId }).eq("id", user.id);
    }

    const session = await stripe.checkout.sessions.create({
      mode: "subscription",
      customer: customerId,
      line_items: [
        {
          quantity: 1,
          price_data: {
            currency: "usd",
            recurring: { interval: "month" },
            unit_amount: 1900,
            product_data: {
              name: "DealDraft Pro",
              description: "Unlimited proposals and contracts. Full dashboard.",
            },
          },
        },
      ],
      metadata: { user_id: user.id },
      subscription_data: { metadata: { user_id: user.id } },
      success_url: `${data.origin}/dashboard?upgraded=1`,
      cancel_url: `${data.origin}/pricing`,
    });

    if (!session.url) throw new Error("Stripe did not return a checkout URL");
    return { url: session.url };
  });

export const createBillingPortalSession = createServerFn({ method: "POST" })
  .inputValidator(z.object({ origin: z.string().url() }))
  .handler(async ({ data }) => {
    const { user, admin } = await getUserFromAuth();
    const { data: profile } = await admin
      .from("profiles")
      .select("stripe_customer_id")
      .eq("id", user.id)
      .maybeSingle();
    if (!profile?.stripe_customer_id) throw new Error("No Stripe customer found.");
    const stripe = await getStripe();
    const portal = await stripe.billingPortal.sessions.create({
      customer: profile.stripe_customer_id,
      return_url: `${data.origin}/settings`,
    });
    return { url: portal.url };
  });
